import html2canvas from 'html2canvas';
import React, { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';

import { viewerZIndices } from '../../../../js/routes/viewer/viewer-z-indices';
import { isDefined } from '../../../../js/utils/variables';
import { SkyviewCss3dContainer } from '../skyview-css-3d-container';

async function captureSkyview(root: HTMLElement): Promise<string> {
  const canvas = await html2canvas(root, {
    backgroundColor: null,
    useCORS: true,
    scale: 2, // Improves the resolution of the resulting image.
  });
  return canvas.toDataURL('image/png');
}

export type SkyviewCanvasHandle = {
  capture: () => Promise<string>;
};

const SkyviewCanvasContainer = forwardRef<SkyviewCanvasHandle>((_, ref) => {
  const rootRef = useRef<HTMLDivElement>(null);

  /* Expose capture() to anyone who holds the ref */
  const capture = useCallback(async () => {
    if (!isDefined(rootRef.current)) {
      throw new Error('SkyviewCanvasContainer not mounted yet');
    }
    return captureSkyview(rootRef.current);
  }, []);

  useImperativeHandle(ref, () => ({ capture }), [capture]);

  /* Expose globally for non-React callers */
  React.useEffect(() => {
    (window as any).getSkyviewScreenshot = capture;
    return () => {
      (window as any).getSkyviewScreenshot = undefined;
    };
  }, [capture]);

  return (
    <Component ref={rootRef}>
      <SkyViewCanvas id="webglrenderercontainer" zIndex={viewerZIndices.webGlRenderer} />
      <SkyviewCss3dContainer />
    </Component>
  );
});

SkyviewCanvasContainer.displayName = 'SkyviewCanvasContainer';

const Component = styled.div<{ assetMenuVisible?: boolean }>`
  position: absolute;

  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  pointer-events: none;
`;

const SkyViewCanvas = styled.div<{ zIndex: number }>`
  position: absolute;

  left: 0;
  top: 0;
  width: 100%;
  height: 100%;

  z-index: ${(props) => props.zIndex};

  pointer-events: all;
`;

export { SkyviewCanvasContainer };
